import React, { useState, useEffect } from "react";
import {
    Alert,
    Button,
    FormControl,
    FormLabel,
    Input,
    Sheet,
    Snackbar,
    Typography,
    Select,
    Option,
} from "@mui/joy";

const AddDeviceDialog = ({ open, onClose, onAddDevice, candidate }) => {
    // Common field
    const [deviceName, setDeviceName] = useState("");
    // Default device type is ONVIF
    const [deviceType, setDeviceType] = useState("onvif");

    // Legacy field
    const [hardwareId, setHardwareId] = useState("");

    // ONVIF-specific fields
    const [ipAddress, setIpAddress] = useState("");
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [prelogin_uuid, setPreloginUuid] = useState("");

    const passwordInputRef = React.useRef(null);

    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Reset state when dialog is opened
    useEffect(() => {
        if (open) {
            if (candidate) {
                setDeviceName(`${candidate.manufacturer_name} ${candidate.model_name}`);
                setIpAddress(candidate.ip_address);
                setLogin('admin');
                setPassword('');
                setTimeout(() => passwordInputRef.current?.focus(), 100); // Focus on password
                console.log('Prelogin UUID: ' + candidate.pre_login_uuid)
                setPreloginUuid(candidate.pre_login_uuid);
            } else {
                setDeviceName("");
                setDeviceType("onvif");
                setHardwareId("");
                setIpAddress("");
                setLogin("");
                setPassword("");
                setLoading(false);
            }
        }
    }, [open, candidate]);

    // MUI Joy's Select passes (event, newValue)
    const handleDeviceTypeChange = (event, newValue) => {
        setDeviceType(newValue);
        // Clear fields not used by the selected type
        if (newValue === "legacy") {
            setIpAddress("");
            setLogin("");
            setPassword("");
        } else if (newValue === "onvif") {
            setHardwareId("");
        }
    };

    const handleAddDevice = async () => {
        if (!deviceName) {
            setSnackbarMessage("Device name is required");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (deviceType === "legacy") {
            if (!hardwareId) {
                setSnackbarMessage("Hardware ID is required for legacy devices");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
        } else if (deviceType === "onvif") {
            if (!ipAddress || !login || !password) {
                setSnackbarMessage("IP Address, Login, and Password are required for ONVIF devices");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
        }

        setLoading(true);
        try {
            const deviceData = {
                type: deviceType,
                name: deviceName,
                ...(deviceType === "legacy"
                    ? { hardwareId }
                    : { ipAddress, login, password , prelogin_uuid}),
            };

            await onAddDevice(deviceData);
            setSnackbarMessage("Device added successfully!");
            setSnackbarSeverity("success");
            onClose(); // Close the dialog on success
        } catch (error) {
            setSnackbarMessage("Failed to add device. Please try again.");
            setSnackbarSeverity("error");
        } finally {
            setLoading(false);
            setSnackbarOpen(true);
        }
    };

    if (!open) return null;

    return (
        <Sheet
            sx={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.level1",
                boxShadow: 24,
                width: "400px",
                padding: "16px",
                borderRadius: "8px",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography level="h6" sx={{ mb: 2 }}>
                Add Device
            </Typography>

            {/* Device type selector */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="deviceType">Device Type</FormLabel>
                <Select
                    id="deviceType"
                    value={deviceType}
                    onChange={handleDeviceTypeChange}
                    // This ensures the selected label remains visible after closing the dropdown
                    renderValue={(selected) =>
                        selected === "legacy" ? "Legacy Camera" : "ONVIF Device"
                    }
                >
                    <Option value="legacy">Base Camera</Option>
                    <Option value="onvif">ONVIF Camera</Option>
                </Select>
            </FormControl>

            {/* Common field: Device Name */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="deviceName">Name</FormLabel>
                <Input
                    id="deviceName"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="Enter Device Name"
                />
            </FormControl>

            {/* Conditional fields based on device type */}
            {deviceType === "legacy" ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="hardwareId">Hardware ID</FormLabel>
                    <Input
                        id="hardwareId"
                        value={hardwareId}
                        onChange={(e) => setHardwareId(e.target.value)}
                        placeholder="Enter Hardware ID"
                    />
                </FormControl>
            ) : (
                <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel htmlFor="ipAddress">IP Address</FormLabel>
                        <Input
                            id="ipAddress"
                            value={ipAddress}
                            onChange={(e) => setIpAddress(e.target.value)}
                            placeholder="Enter IP Address"
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel htmlFor="login">Login</FormLabel>
                        <Input
                            id="login"
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            placeholder="Enter Login"
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel htmlFor="password">Password</FormLabel>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            inputRef={passwordInputRef}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter Password"
                        />
                    </FormControl>
                </>
            )}

            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleAddDevice} disabled={loading}>
                    {loading ? "Adding..." : "Add"}
                </Button>
            </div>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Sheet>
    );
};

export default AddDeviceDialog;
